import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { format, startOfDay, sub } from 'date-fns';
import dateFNSPTBr from 'date-fns/locale/pt-BR';
import { FaTruckFront } from 'react-icons/fa6';
import moment from 'moment';
import { DataGrid, GridColDef, ptBR } from '@mui/x-data-grid';

import AppToolBar from '../../components/AppToolBar';
import BarTop from '../../components/BarTop';
import { PageLoading } from '../../../../components/PageLoading/PageLoading';
import { LogOperationTypes, driverLogService } from '../../../../domain';

import { QueueHeader } from '../components/QueueHeader';
import { DriverLogRow } from './DriverLogRow';
import api from '../../../../services/api';

import './styles.css';
import './styles.scss';

interface LogMessage {
  operation: string;
  created_at: Date;
  content?: string;
  contentPosition?: 'center' | 'end';
}

interface Scheduling {
  id: number;
  qtyOrdered: number;
  qtyTransported: any;
  createAt: string;
  expedInitialDate: any;
  expedDate: any;
  expirationDate: string;
  status: boolean;
  reason: string;
  driver: {
    id: number;
    name: string;
    isEnabled: boolean;
    lastBlockDate: any;
    deletedAt: any;
    createdAt: string;
    updatedAt: string;
  };
  truck: {
    id: number;
    licensePlate: string;
    renavam: string;
    status: boolean;
    isEnabled: boolean;
    owner: string;
    implementType: string;
    shaftQuantity: number;
    grossCapacity: number;
    netCapacity: number;
    color: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: any;
  };
  order: {
    id: number;
    code: string;
    status: any;
    qtyTransported: number;
    createAt: string;
    enable: boolean;
    customer: {
      id: number;
      code: number;
      name: string;
      cnpj: string;
      uf: string;
      street: string;
      number: string;
      city: string;
      district: string;
      zipCode: string;
      stateRegistration: string;
      createdAt: string;
      updatedAt: string;
      deletedAt: any;
    };
  };
  company: {
    id: number;
    name: string;
    cnpj: string;
    company: string;
    uf: string;
    street: string;
    number: string;
    complement: string;
    city: string;
    district: string;
    zipCode: string;
    status: boolean;
    website: string;
    logo: string;
  };
}

interface Logs {
  id: number;
  title: string;
  company: {
    id: any;
    name: any;
  };
  details: {
    key: string;
    value: any;
  }[];
  created_at: string;
}

const typeLogMessageMap: Record<LogOperationTypes, string> = {
  LOGIN: 'Logou no sistema',
  IN_QUEUE: 'Entrou na Fila',
  OUT_QUEUE: 'Saiu da Fila',
  ATT_QUEUE: 'Atendido pela expedição',
  FAU_QUEUE: 'Faltoso pela expedição',
  TOL_QUEUE: 'Faltoso por tolerância',
  REG_DRIVER: 'Resgistro Motorista',
  CAD_DRIVER: 'Cadastro Motorista',
  CAD_TRUCK: 'Cadastrou Caminhão',
  EDIT_TRUCK: 'Editou Caminhão',
  BIND_TRUNCK: 'Vinculou Caminhão',
  UNBIND_TRUCK: 'Desvinculou Caminhão',
  IN_ORDER: 'Adicionado no Pedido',
  OUT_COMPANY: 'Mudou de empresa',
  DEL_ADM: 'Excluiu do Sistema /Admin',
  DEL_APP: 'Excluiu do Sistema /App',
  READ_POPUP: 'Visualizou Popup',
  CHECK_IN: 'fez check-in',
  CHECK_OUT: 'cancelou check-in',
};

interface DriverLogsProps {
  history?: boolean;
}
export function DriverLogs({ history }: DriverLogsProps) {
  const [data, setData] = useState<Scheduling[]>([]);
  const [rows, setRows] = useState<Scheduling[]>([]);

  const date7DaysAgo = sub(new Date(), { days: 7 });
  const driverId = sessionStorage.getItem('@AppDriver:id');

  const [loading, setLoading] = useState(false);

  const [maxDate, setMaxDate] = useState(() => {
    const today = new Date();
    const maxDate = format(today, 'yyyy-MM-dd');
    return maxDate;
  });

  const [minDate, setMinDate] = useState(() => {
    const minDate = format(startOfDay(date7DaysAgo), 'yyyy-MM-dd');
    return minDate;
  });

  const columns = useMemo(
    () =>
      [
        {
          field: 'id',
          headerName: 'Id',
          sortable: false,
          width: 60,
          align: 'center',
        },
        {
          field: 'title',
          headerName: 'Título',
          sortable: false,
          width: 200,
          renderCell: (params) => {
            return <p>{params.row.title}</p>;
          },
        },
        {
          field: 'company',
          headerName: 'Empresa',
          width: 200,
          renderCell: (params) => {
            return <p>{params.row.company.name}</p>;
          },
        },
      ] as GridColDef<Logs>[],
    []
  );

  function handleLogMessage({ operation, created_at, content, contentPosition = 'end' }: LogMessage) {
    let message = '';

    const logDate = format(new Date(created_at), "dd/MM/yyyy 'às' HH:mm", {
      locale: dateFNSPTBr,
    });

    if (content) {
      if (contentPosition === 'center') {
        message = `Motorista ${operation} ${content} ${logDate}`;
      } else {
        message = `Motorista ${operation} ${logDate} ${content}`;
      }
    } else {
      message = `Motorista ${operation} ${logDate}`;
    }

    return message;
  }
  function checkType(key: LogOperationTypes) {
    return typeLogMessageMap[key] || 'Logou no sistema';
  }
  /**
   * @param logs DriverLog[]
   */
  function formatLogs(logs: any[]) {
    return logs.map((item: any, index) => {
      item.id = index;
      const keys = Object.keys(item.data);
      const values = Object.values(item.data) as unknown as any;

      const operationType = item.operation_type as LogOperationTypes;
      const operation = checkType(operationType);

      const date = new Date(item.createdAt);
      const details = [];

      let title = handleLogMessage({
        created_at: date,
        operation,
      });

      if (item.operation_type === 'IN_QUEUE') {
        const licensePlate = item.data?.truck?.licensePlate;
        const existsLicensePlate = licensePlate;

        const content = existsLicensePlate ? `com a Placa ${licensePlate}` : `como Autônomo`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'IN_ORDER') {
        const code = String(item.data?.scheduling?.order?.code);

        title = handleLogMessage({
          created_at: date,
          operation,
          content: code,
          contentPosition: 'center',
        });
      }

      if (operationType === 'OUT_COMPANY') {
        const content = `Para ${item.data?.companyTo?.company}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'CAD_TRUCK') {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'EDIT_TRUCK') {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'UNBIND_TRUCK') {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'BIND_TRUNCK') {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'BIND_TRUNCK') {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content,
        });
      }

      if (operationType === 'READ_POPUP') {
        const popupData = item.data.popup;

        const popupTitle = popupData.title;
        const popupLabel = popupData.label;

        const popupTitleIsEmpty = popupTitle === '';
        const content = !popupTitleIsEmpty ? popupTitle : popupLabel;

        title = handleLogMessage({
          operation,
          created_at: date,
          content,
          contentPosition: 'center',
        });
      }

      if (operationType === 'CHECK_IN') {
        title = handleLogMessage({
          operation,
          content: 'em',
          contentPosition: 'center',
          created_at: date,
        });
      }

      if (operationType === 'CHECK_OUT') {
        title = handleLogMessage({
          operation,
          content: 'em',
          contentPosition: 'center',
          created_at: date,
        });
      }

      for (let index = 0; index < keys.length; index++) {
        details.push({ key: keys[index], value: values[index] });
      }

      const companyId = item.data?.company?.id;
      const companyName = item.data?.company?.company;

      const data = {
        id: index + 1,
        title: title,
        company: { id: companyId, name: companyName },
        details: details,
        created_at: date.toLocaleDateString(),
      };

      return data;
    });
  }
  async function getLogs() {
    if (!driverId) return;

    try {
      setLoading(true);

      const logs = await driverLogService.getList({
        driverId,
        period: {
          startDate: minDate,
          endDate: maxDate,
        },
      });

      setRows(formatLogs(logs) as any);

      toast.success('Consulta finalizada!');
    } catch (error) {
      toast.error('Erro ao consultar!');
    } finally {
      setLoading(false);
    }
  }

  const handleMinMaxDate = (date: string, type: 'min' | 'max') => {
    const formmatPattern = 'YYYY-MM-DD';
    const formattedDate = moment(date).format(formmatPattern);
    const matches = Object.freeze({
      min: () => {
        if (formattedDate > maxDate) return toast.error('A data inicial não pode ser maior que a data final.');

        setMinDate(formattedDate);
      },
      max: () => {
        if (formattedDate < minDate) return toast.error('A data final não pode ser menor que a data inicial.');

        setMaxDate(formattedDate);
      },
    });
    const keys = Object.keys(matches);

    if (!keys.includes(type)) return console.error(`Invalid type: ${type}. Accepteds: ${keys.join(', ')}`);

    matches[type]();
  };

  async function getDriverHistory() {
    if (!driverId) return;

    try {
      setLoading(true);

      const { data } = await api.get<Scheduling[]>(`/schedulingDeleted/driver/${driverId}`, { params: { minDate, maxDate } });
      const filteredData = data.filter((item) => item.reason === 'TEMPO EXPIRADO');

      setData(filteredData);

      if (!toast.isActive('driver-history-toast')) {
        toast.success('Consulta finalizada!', { toastId: 'driver-history-toast' });
      }
    } catch (error) {
      toast.error('Erro ao consultar!');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleFilter = () => {
    if (!history) return getLogs();
    getDriverHistory();
  };

  useEffect(() => {
    if (!history) {
      getLogs();

      return;
    }

    getDriverHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const queueHeaderTitle = history ? 'Autorizações Expiradas' : 'Registros do motorista';

  return (
    <div className="container">
      <PageLoading loading={loading} onCloseLoading={() => setLoading(false)} />

      <BarTop />
      <div className="header">
        <AppToolBar pathToBack="/" isStatic={false} />
      </div>

      <div className="driver__log">
        <QueueHeader Icon={FaTruckFront} className="driver__log--header">
         {queueHeaderTitle}
        </QueueHeader>

        <div className="driver__log--inputs">
          <label>Periodos:</label>

          <div>
            <input
              className="driver__input--date"
              type="text"
              placeholder="Data Inicial"
              onFocus={(e) => (e.target.type = 'date')}
              onBlur={(e) => (e.target.type = 'text')}
              value={minDate}
              onChange={(e) => handleMinMaxDate(e.target.value, 'min')}
            />

            <input
              className="driver__input--date"
              type="text"
              placeholder="Data Final"
              onFocus={(e) => (e.target.type = 'date')}
              onBlur={(e) => (e.target.type = 'text')}
              value={maxDate}
              onChange={(e) => handleMinMaxDate(e.target.value, 'max')}
            />

            <button type="button" className="driver__input--btn" onClick={handleFilter}>
              {loading ? 'Carregando' : 'Aplicar'}
            </button>
          </div>
        </div>

        <div className="driver__log--wrapper">
          {history && (
            <>
              {data.length === 0 && (
                <div className="driver__log--empty">
                  <p>Não há histórico no momento</p>
                </div>
              )}

              {data.length > 0 &&
                data.map((item, i) => {
                  return (
                    <DriverLogRow
                      order={item.order.code}
                      transporter={item.company.name}
                      client={item.order.customer.name}
                      createAt={moment(item.createAt).format('DD/MM/YYYY - HH:mm')}
                      expirationDate={item.expirationDate}
                      key={i}
                    />
                  );
                })}
            </>
          )}

          {!history && (
            <DataGrid
              autoHeight={true}
              rows={rows}
              rowHeight={80}
              columns={columns as any}
              disableColumnMenu
              pageSize={10}
              rowsPerPageOptions={[10]}
              headerHeight={30}
              localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
              disableSelectionOnClick
              sx={{ marginBottom: '20px' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
